import { useState } from 'react';
import { rupee } from './financeConfig';
import styles from './supFinance.module.css';

/*
 * One series bar chart (sales per day / week / month) with a hover tooltip.
 *   points: [{ label, value, sub }]   sub: extra line in the tooltip
 */
const W = 720;
const H = 200;
const PAD = { top: 12, right: 8, bottom: 26, left: 56 };

const niceMax = (v) => {
    if (v <= 0) return 1;
    const p = 10 ** Math.floor(Math.log10(v));
    return Math.ceil(v / p / (v / p > 5 ? 2 : 1)) * p * (v / p > 5 ? 2 : 1);
};
const short = (n) =>
    n >= 1e7
        ? `₹${(n / 1e7).toFixed(1)}Cr`
        : n >= 1e5
          ? `₹${(n / 1e5).toFixed(1)}L`
          : n >= 1e3
            ? `₹${Math.round(n / 1e3)}k`
            : `₹${n}`;

export default function TrendChart({ points = [], title }) {
    const [hover, setHover] = useState(null);
    const max = niceMax(Math.max(0, ...points.map((p) => p.value)));
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const slot = points.length ? innerW / points.length : innerW;
    const barW = Math.max(2, Math.min(28, slot - 2));
    const y = (v) => PAD.top + innerH - (v / max) * innerH;
    const ticks = [0, max / 2, max];
    const labelEvery = Math.ceil(points.length / 12);

    return (
        <figure className={styles.chart}>
            <figcaption className={styles.chartTitle}>{title}</figcaption>
            {points.length === 0 ? (
                <p className={styles.empty}>No bookings in this period.</p>
            ) : (
                <div className={styles.chartBox}>
                    <svg viewBox={`0 0 ${W} ${H}`} role='img' aria-label={title} preserveAspectRatio='none'>
                        {ticks.map((t) => (
                            <g key={t}>
                                <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className={styles.grid} />
                                <text x={PAD.left - 6} y={y(t) + 4} textAnchor='end' className={styles.axis}>
                                    {short(t)}
                                </text>
                            </g>
                        ))}
                        {points.map((p, i) => {
                            const x = PAD.left + i * slot + (slot - barW) / 2;
                            const h = Math.max(p.value > 0 ? 2 : 0, (p.value / max) * innerH);
                            return (
                                <g key={p.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                                    {/* hit area bigger than the bar */}
                                    <rect
                                        x={PAD.left + i * slot}
                                        y={PAD.top}
                                        width={slot}
                                        height={innerH}
                                        fill='transparent'
                                    />
                                    <rect
                                        x={x}
                                        y={PAD.top + innerH - h}
                                        width={barW}
                                        height={h}
                                        rx={Math.min(4, barW / 2)}
                                        className={hover === i ? styles.barHover : styles.bar}
                                    />
                                    {i % labelEvery === 0 && (
                                        <text x={x + barW / 2} y={H - 8} textAnchor='middle' className={styles.axis}>
                                            {p.label}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                    </svg>
                    {hover !== null && (
                        <div
                            className={styles.tooltip}
                            style={{ left: `${((PAD.left + hover * slot + slot / 2) / W) * 100}%` }}
                        >
                            <strong>{points[hover].label}</strong>
                            <span>Sales {rupee(points[hover].value)}</span>
                            {points[hover].sub && <span>{points[hover].sub}</span>}
                        </div>
                    )}
                </div>
            )}
        </figure>
    );
}
